import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { positionsAPI } from '../services/positionsAPI';
import { achievementsAPI } from '../services/api';
import { Position, Achievement } from '../types';
import { getErrorMessage } from '../utils/errorUtils';
import PageHeader from '../components/PageHeader';
import DataTable, { Column } from '../components/DataTable';
import SidePanel from '../components/SidePanel';
import { API_BASE } from '../utils/fileUtils';
import '../styles/global.css';

type PositionWithAchievements = Position & {
  achievements: Array<{
    id: number;
    achievement_id: number;
    achievement_title: string;
    achievement?: Achievement;
    is_required: boolean;
    order_index?: number;
  }>;
};

type AchievementPositionData = {
  achievement_id: number;
  is_required: boolean;
  order_index?: number;
};

type PosAchRow = PositionWithAchievements['achievements'][number] & { _index: number };

const AdminPositions: React.FC = () => {
  const { user: currentUser } = useAuth();
  
  const [positions, setPositions] = useState<PositionWithAchievements[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [editingPosition, setEditingPosition] = useState<PositionWithAchievements | null>(null);
  const [editingAchievements, setEditingAchievements] = useState<PositionWithAchievements | null>(null);
  const [uploading, setUploading] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState<'position' | 'achievements'>('position');
  const [editPositionIndex, setEditPositionIndex] = useState(-1);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    level: 1,
    next_position_id: undefined as number | undefined,
  });

  const [achievementFormData, setAchievementFormData] = useState<AchievementPositionData>({
    achievement_id: 0,
    is_required: true,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);
      
      const [positionsData, achievementsData] = await Promise.all([
        positionsAPI.getPositions(),
        achievementsAPI.getAchievements()
      ]);

      const positionsWithAchievements: PositionWithAchievements[] = await Promise.all(
        positionsData.map(async (position) => {
          try {
            const positionAchievements = await positionsAPI.getPositionAchievements(position.id);
            const achievementsWithDetails = positionAchievements.map((pa) => {
              const achievement = achievementsData.find((a) => a.id === pa.achievement_id);
              return {
                ...pa,
                achievement_title: achievement?.title || `ID: ${pa.achievement_id}`,
                achievement,
              };
            });
            return {
              ...position,
              achievements: achievementsWithDetails,
            };
          } catch (err) {
            console.error(`Ошибка загрузки ачивок для должности ${position.id}:`, err);
            return {
              ...position,
              achievements: [],
            };
          }
        })
      );

      positionsWithAchievements.sort((a, b) => a.level - b.level);
      setPositions(positionsWithAchievements);
      setAchievements(achievementsData);
    } catch (err: any) {
      setError(getErrorMessage(err));
      console.error('Ошибка загрузки данных:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreatePosition = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setUploading(true);
      setError(undefined);

      const positionData = {
        ...formData,
        next_position_id: formData.next_position_id === undefined || formData.next_position_id === 0 
          ? undefined 
          : formData.next_position_id,
      };

      await positionsAPI.createPosition(positionData);
      closePanel();
      loadData();
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const handleUpdatePosition = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPosition) return;

    try {
      setUploading(true);
      setError(undefined);

      const positionData = {
        title: formData.title,
        description: formData.description,
        level: formData.level,
        next_position_id: formData.next_position_id === undefined || formData.next_position_id === 0 
          ? undefined 
          : formData.next_position_id,
      };

      await positionsAPI.updatePosition(editingPosition.id, positionData);
      closePanel();
      loadData();
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePosition = async (positionId: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту должность? Все связанные ачивки также будут отвязаны.')) {
      return;
    }

    try {
      setUploading(true);
      await positionsAPI.deletePosition(positionId);
      loadData();
    } catch (err: any) {
      setError(getErrorMessage(err));
      console.error('Ошибка удаления должности:', err);
    } finally {
      setUploading(false);
    }
  };

  const startEdit = (position: PositionWithAchievements) => {
    const idx = positions.findIndex(p => p.id === position.id);
    setEditingPosition(position);
    setEditPositionIndex(idx);
    setFormData({
      title: position.title,
      description: position.description || '',
      level: position.level,
      next_position_id: position.next_position_id ?? undefined,
    });
    setPanelMode('position');
    setPanelOpen(true);
  };

  const openCreatePanel = () => {
    setEditingPosition(null);
    setEditPositionIndex(-1);
    setFormData({ title: '', description: '', level: 1, next_position_id: undefined });
    setPanelMode('position');
    setPanelOpen(true);
  };

  const closePanel = () => {
    setPanelOpen(false);
    setEditingPosition(null);
    setEditingAchievements(null);
    setEditPositionIndex(-1);
    setFormData({ title: '', description: '', level: 1, next_position_id: undefined });
    setAchievementFormData({ achievement_id: 0, is_required: true });
  };

  const handleFormChange = (field: string, value: string | number | undefined) => {
    if (field === 'next_position_id') {
      if (value === '' || value === undefined || value === null) {
        setFormData(prev => ({ ...prev, [field]: undefined }));
      } else {
        setFormData(prev => ({ ...prev, [field]: Number(value) }));
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleManageAchievements = (position: PositionWithAchievements) => {
    setEditingAchievements(position);
    setAchievementFormData({ achievement_id: 0, is_required: true });
    setPanelMode('achievements');
    setPanelOpen(true);
  };

  const handleAddAchievementToPosition = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAchievements) return;

    try {
      setUploading(true);
      setError(undefined);

      const alreadyAdded = editingAchievements.achievements.some(
        (a) => a.achievement_id === achievementFormData.achievement_id
      );

      if (alreadyAdded) {
        setError('Эта ачивка уже добавлена к должности');
        return;
      }

      const achievementData = {
        position_id: editingAchievements.id,
        achievement_id: achievementFormData.achievement_id,
        is_required: achievementFormData.is_required,
        order_index: achievementFormData.order_index,
      };

      await positionsAPI.addAchievementToPosition(editingAchievements.id, achievementData);
      setAchievementFormData({ achievement_id: 0, is_required: true, order_index: undefined });
      
      await loadData();

      const updatedPosition = positions.find((p) => p.id === editingAchievements.id);
      if (updatedPosition) {
        setEditingAchievements(updatedPosition);
      }
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAchievementFromPosition = async (positionId: number, achievementId: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту ачивку из должности?')) {
      return;
    }

    try {
      setUploading(true);
      await positionsAPI.removeAchievementFromPosition(positionId, achievementId);
      
      await loadData();

      if (editingAchievements && editingAchievements.id === positionId) {
        const updatedPosition = positions.find((p) => p.id === positionId);
        if (updatedPosition) {
          setEditingAchievements(updatedPosition);
        }
      }
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const getNextPositionTitle = (nextPositionId?: number) => {
    if (!nextPositionId) return 'Не указана';
    const nextPosition = positions.find((p) => p.id === nextPositionId);
    return nextPosition ? nextPosition.title : `ID: ${nextPositionId}`;
  };

  const getAvailableNextPositions = (
    currentPosition?: PositionWithAchievements,
    isEditing: boolean = false
  ) => {
    if (!currentPosition) {
      return positions.filter((p) => {
        const isAlreadyNext = positions.some((otherPos) => otherPos.next_position_id === p.id);
        return !isAlreadyNext;
      });
    }

    const excludedIds = new Set<number>();
    
    excludedIds.add(currentPosition.id);
    
    const checkDependencies = (pos: PositionWithAchievements) => {
      let checkPos = pos;
      while (checkPos.next_position_id) {
        const nextPos = positions.find((p) => p.id === checkPos.next_position_id);
        if (nextPos) {
          excludedIds.add(nextPos.id);
          checkPos = nextPos;
        } else {
          break;
        }
      }
    };
    
    checkDependencies(currentPosition);

    const available = positions.filter((p) => {
      // ВАЖНО: Если редактируем и это текущая следующая должность - включаем СРАЗУ
      if (isEditing && currentPosition.next_position_id === p.id) {
        return true;
      }

      // Не включаем исключенные
      if (excludedIds.has(p.id)) {
        return false;
      }

      // Проверяем уровень
      return p.level >= currentPosition.level;
    });

    return available;
  };

  if (currentUser?.role !== 'admin') {
    return (
      <div className="container">
        <div className="error">У вас нет доступа к этому разделу</div>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  const positionColumns: Column<PositionWithAchievements>[] = [
    {
      key: 'title',
      header: 'Должность',
      render: (p) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <span className="status-badge status-submitted" style={{ flexShrink: 0 }}>Ур. {p.level}</span>
          <strong style={{ color: 'var(--primary-orange)' }}>{p.title}</strong>
        </div>
      ),
    },
    {
      key: 'next_position_id',
      header: 'Следующая',
      render: (p) => (
        p.next_position_id
          ? <span className="status-badge status-approved">{getNextPositionTitle(p.next_position_id)}</span>
          : <span className="status-badge status-pending">—</span>
      ),
    },
    {
      key: 'achievements',
      header: 'Ачивки',
      width: '120px',
      render: (p) => (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          <span className="status-badge status-rejected">{p.achievements.filter((a) => a.is_required).length} обяз.</span>
          <span className="status-badge status-pending">{p.achievements.length} всего</span>
        </div>
      ),
    },
  ];

  const renderExpandedPosition = (p: PositionWithAchievements) => (
    <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div className="exp-grid" style={{ flex: 1 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <div className="exp-label">Описание</div>
          <div className="exp-value">{p.description || '—'}</div>
        </div>
        <div>
          <div className="exp-label">Создано</div>
          <div className="exp-value">{new Date(p.created_at).toLocaleDateString('ru-RU')}</div>
        </div>
        <div>
          <div className="exp-label">ID</div>
          <div className="exp-value">#{p.id}</div>
        </div>
      </div>
      <div className="exp-actions" onClick={(e) => e.stopPropagation()}>
        <button className="btn btn-primary btn-sm" onClick={() => startEdit(p)} disabled={uploading}>Правка</button>
        <button className="btn btn-outline btn-sm" onClick={() => handleManageAchievements(p)} disabled={uploading}>Ачивки</button>
        <button className="btn btn-danger btn-sm" onClick={() => handleDeletePosition(p.id)} disabled={uploading}>Удалить</button>
      </div>
    </div>
  );

  const posAchColumns: Column<PosAchRow>[] = [
    { key: '_index', header: '#', width: '40px' },
    {
      key: 'achievement_title',
      header: 'Ачивка',
      render: (row) => (
        <>
          <strong>{row.achievement_title}</strong>
          {row.achievement?.icon_path && (
            <div>
              <img
                src={`${API_BASE}/uploads/${row.achievement.icon_path}`}
                alt={row.achievement_title}
                style={{ width: '24px', height: '24px', marginRight: '5px' }}
              />
            </div>
          )}
        </>
      ),
    },
    {
      key: 'description',
      header: 'Описание',
      render: (row) => String(row.achievement?.description || '-'),
    },
    {
      key: 'order_index',
      header: 'Порядок',
      width: '80px',
      render: (row) => String(row.order_index || '-'),
    },
    {
      key: 'is_required',
      header: 'Обязательная',
      width: '110px',
      render: (row) => (
        <span className={`status-badge ${row.is_required ? 'status-approved' : 'status-pending'}`}>
          {row.is_required ? 'Да' : 'Нет'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Действия',
      width: '90px',
      render: (row) => (
        <button
          className="btn btn-danger btn-sm"
          onClick={() => editingAchievements && handleRemoveAchievementFromPosition(editingAchievements.id, row.achievement_id)}
          disabled={uploading}
        >
          Удалить
        </button>
      ),
    },
  ];

  const posAchData: PosAchRow[] = editingAchievements
    ? editingAchievements.achievements.map((a, i) => ({ ...a, _index: i + 1 }))
    : [];

  return (
    <div className="container" style={{ paddingTop:'1.5rem', paddingBottom:'2rem' }}>
      <PageHeader
        title="Управление должностями"
        actions={
          <button className="btn btn-primary" onClick={openCreatePanel} disabled={uploading}>
            + Создать должность
          </button>
        }
      />

      {error && <div className="error">{error}</div>}

      {/* Панель: создать / редактировать должность */}
      <SidePanel
        isOpen={panelOpen && panelMode === 'position'}
        onClose={closePanel}
        title={editingPosition ? `Редактировать: ${editingPosition.title}` : 'Создать должность'}
        onPrev={editingPosition ? () => startEdit(positions[editPositionIndex - 1]) : undefined}
        onNext={editingPosition ? () => startEdit(positions[editPositionIndex + 1]) : undefined}
        hasPrev={editPositionIndex > 0}
        hasNext={editPositionIndex < positions.length - 1}
        navLabel={editingPosition ? `${editPositionIndex + 1} из ${positions.length}` : undefined}
      >
        <form onSubmit={editingPosition ? handleUpdatePosition : handleCreatePosition}>
          <div className="form-group">
            <label className="form-label">Название</label>
            <input
              type="text"
              className="form-control"
              value={formData.title}
              onChange={(e) => handleFormChange('title', e.target.value)}
              placeholder="Например: Менеджер по продажам"
              required
              disabled={uploading}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Описание</label>
            <textarea
              className="form-control"
              rows={4}
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              placeholder="Опишите требования к должности"
              required
              disabled={uploading}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Уровень (1–10)</label>
            <input
              type="number"
              className="form-control"
              value={formData.level}
              onChange={(e) => handleFormChange('level', parseInt(e.target.value) || 1)}
              min={1} max={10}
              required
              disabled={uploading}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Следующая должность</label>
            <select
              className="form-control"
              value={formData.next_position_id !== undefined ? String(formData.next_position_id) : ''}
              onChange={(e) => handleFormChange('next_position_id', e.target.value ? parseInt(e.target.value) : undefined)}
              disabled={uploading}
            >
              <option value="">Не выбрано</option>
              {getAvailableNextPositions(editingPosition || undefined, !!editingPosition).map((p) => (
                <option key={p.id} value={String(p.id)}>{p.title} (Ур. {p.level})</option>
              ))}
            </select>
            <small className="text-muted">
              {editingPosition
                ? `Текущий уровень: ${formData.level}. Следующая — такого же уровня или выше.`
                : `Уровень: ${formData.level}. Рекомендуется выбирать должность того же или выше уровня.`}
            </small>
          </div>
          <div className="side-panel__footer">
            <button type="button" className="btn btn-secondary" onClick={closePanel} disabled={uploading}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={uploading}>
              {uploading ? 'Сохранение...' : editingPosition ? 'Сохранить' : 'Создать'}
            </button>
          </div>
        </form>
      </SidePanel>

      {/* Панель: управление ачивками должности */}
      <SidePanel
        isOpen={panelOpen && panelMode === 'achievements'}
        onClose={closePanel}
        title={editingAchievements ? `Ачивки: ${editingAchievements.title}` : 'Ачивки'}
        width="600px"
      >
        {editingAchievements && (
          <>
            <form onSubmit={handleAddAchievementToPosition}>
              <div className="form-group">
                <label className="form-label">Добавить ачивку</label>
                <select
                  className="form-control"
                  value={achievementFormData.achievement_id}
                  onChange={(e) => setAchievementFormData({ ...achievementFormData, achievement_id: parseInt(e.target.value) })}
                  disabled={uploading}
                  required
                >
                  <option value={0}>Выберите ачивку</option>
                  {achievements
                    .filter((ach) => !editingAchievements.achievements.some((pa) => pa.achievement_id === ach.id))
                    .map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={achievementFormData.is_required}
                    onChange={(e) => setAchievementFormData({ ...achievementFormData, is_required: e.target.checked })}
                    disabled={uploading}
                  />
                  Обязательна для повышения
                </label>
              </div>
              <button type="submit" className="btn btn-primary" disabled={uploading}>
                {uploading ? 'Добавление...' : 'Добавить'}
              </button>
            </form>

            <div style={{ marginTop: '1.5rem', borderTop: '1px solid var(--border-light)', paddingTop: '1rem' }}>
              <h4 style={{ marginBottom: '0.75rem', fontWeight: 600, fontSize: '0.95rem' }}>
                Привязанные ачивки ({editingAchievements.achievements.length})
              </h4>
              <DataTable
                columns={posAchColumns}
                data={posAchData}
                rowKey={(r) => r.id}
                emptyText="Нет привязанных ачивок"
              />
            </div>
          </>
        )}
      </SidePanel>


      <DataTable
        columns={positionColumns}
        data={positions}
        rowKey={(p) => p.id}
        emptyText="Нет должностей. Создайте первую!"
        minWidth="460px"
        renderExpanded={renderExpandedPosition}
      />
    </div>
  );
};

export default AdminPositions;
